/**
 * Adaptive quality governor (CLAUDE.md, performance budgets).
 *
 * Rules, verbatim from the spec:
 *   - watch the rolling 1 %-low fps,
 *   - step **down** when it stays below 55 for 3 s,
 *   - step **up**   when it stays above 80 for 10 s.
 *
 * Four additions the spec implies but does not spell out. The first two stop the tier
 * oscillating on a machine that sits right on the boundary; the last two exist because the
 * milestone-1 test flight found that, as literally written, the step-up rule can essentially
 * never fire:
 *
 *   1. **Settle period** after any change, during which no rule may fire and the fps window is
 *      ignored (the frame after a tier switch is always slow — new shaders, new render targets).
 *
 *   2. **Escalating penalty**: each time the governor has to undo an upgrade, the dwell time
 *      required before retrying that upgrade doubles, up to a cap. A laptop that cannot hold
 *      High settles at Medium instead of flapping between the two forever.
 *
 *   3. **The upgrade dwell is a credit, not a stopwatch.** Reported symptom: a steady 120 fps
 *      session that sat on "low (auto)" forever. The 1 %-low is already the 99th percentile
 *      over a 5 s window, so a single hitch depresses it for a full five seconds. Restarting a
 *      10 s stopwatch every time that happened meant the dwell only completed if the browser
 *      went 15 s without a GC, a compositor stall or a texture upload — which it never does.
 *      So the governor accumulates credit while the rule holds and drains it while it does
 *      not: the dwell now measures ten seconds of *sustained* headroom, and a one-second dip
 *      costs one second rather than everything. The downgrade rule keeps its stopwatch on
 *      purpose — a recovery really is evidence against downgrading, and the safety rule should
 *      stay eager to rearm.
 *
 *   4. **The upgrade threshold is capped by what the display can actually deliver.** On a
 *      vsync-locked 60 Hz panel every frame is 16.67 ms, so the 1 %-low reads 60 and can never
 *      exceed 80 no matter how much GPU headroom there is — the spec's rule is unsatisfiable,
 *      and the reference laptop in CLAUDE.md (an M1 Air) is exactly such a panel. The
 *      threshold is therefore `min(80, achievableFps × 0.97)`, floored a little above the
 *      downgrade trigger so the two can never cross. Where 80 fps is reachable — any 90 Hz+
 *      display — the spec's number is used unchanged. The estimate is the *modal* frame time,
 *      so a scene that is GPU-bound at 45 fps reports 45 and does not get read as headroom.
 *
 * `updateFixed` / `updateFrame`: neither — `update(nowMs, lowFps, achievableFps)` is called
 * once per frame from the loop's after-frame slot. It is allocation-free.
 */

import type { QualityManager } from './quality.js';

/**
 * Sentinel for "this rule's timer is not armed".
 *
 * −1 rather than NaN: a NaN read back out of a numeric field is materialised as a boxed
 * HeapNumber by V8, which turns a once-per-frame check into a once-per-frame allocation.
 * Timestamps are never negative, so −1 is unambiguous and stays unboxed.
 */
const NOT_ARMED = -1;

export interface GovernorOptions {
  /** Step down when the 1 %-low sits below this [fps]. */
  downgradeBelowFps?: number;
  /** Step up when the 1 %-low sits above this [fps]. */
  upgradeAboveFps?: number;
  /** Continuous time below the floor before stepping down [ms]. */
  downgradeDwellMs?: number;
  /** Accumulated time above the ceiling before stepping up [ms]. */
  upgradeDwellMs?: number;
  /** Quiet period after any tier change [ms]. */
  settleMs?: number;
  /** Ceiling on the doubling upgrade penalty [ms]. */
  maxUpgradeDwellMs?: number;
  /**
   * Quiet period after {@link QualityGovernor.start} [ms]. Must exceed the profiler's rolling
   * window, so that by the time the governor takes an interest the boot frames — shader
   * compilation, first-use pipeline creation — have aged out of the statistics entirely.
   */
  warmupMs?: number;
  /**
   * How fast accumulated upgrade credit drains while the rule does not hold, relative to how
   * fast it accumulates. 1 = symmetric.
   */
  upgradeCreditDrainRate?: number;
  /** Fraction of the achievable frame rate the upgrade threshold may not exceed. */
  upgradeRefreshFraction?: number;
  /** The upgrade threshold never comes closer than this to the downgrade floor [fps]. */
  minUpgradeMarginFps?: number;
  /** Largest inter-update gap that may be credited [ms]; guards against tab-restore jumps. */
  maxCreditStepMs?: number;
}

export interface GovernorDecision {
  /** What the governor did on the last `update()`. */
  action: 'none' | 'down' | 'up';
  nowMs: number;
  lowFps: number;
}

export class QualityGovernor {
  private readonly quality: QualityManager;
  readonly downgradeBelowFps: number;
  readonly upgradeAboveFps: number;
  readonly downgradeDwellMs: number;
  readonly baseUpgradeDwellMs: number;
  readonly settleMs: number;
  readonly maxUpgradeDwellMs: number;
  readonly warmupMs: number;
  readonly upgradeCreditDrainRate: number;
  readonly upgradeRefreshFraction: number;
  readonly minUpgradeMarginFps: number;
  readonly maxCreditStepMs: number;

  private belowSinceMs = NOT_ARMED;
  /** Accumulated time the upgrade rule has held [ms]; see addition 3 in the file comment. */
  private upgradeCreditMs = 0;
  private lastUpdateMs = NOT_ARMED;
  private settleUntilMs = Number.NEGATIVE_INFINITY;
  private warmupUntilMs = Number.NEGATIVE_INFINITY;
  private upgradeDwellMs: number;
  /** Set when the governor raises the tier, so a following downgrade counts as a failed try. */
  private lastActionWasUpgrade = false;
  private downgradeCountValue = 0;
  private upgradeCountValue = 0;
  /** Threshold used by the most recent `update()` [fps] — for the overlay. */
  private effectiveUpgradeFpsValue: number;

  constructor(quality: QualityManager, options: GovernorOptions = {}) {
    this.quality = quality;
    this.downgradeBelowFps = options.downgradeBelowFps ?? 55;
    this.upgradeAboveFps = options.upgradeAboveFps ?? 80;
    this.downgradeDwellMs = options.downgradeDwellMs ?? 3_000;
    this.baseUpgradeDwellMs = options.upgradeDwellMs ?? 10_000;
    this.settleMs = options.settleMs ?? 2_000;
    this.maxUpgradeDwellMs = options.maxUpgradeDwellMs ?? 120_000;
    this.warmupMs = options.warmupMs ?? 6_000;
    this.upgradeCreditDrainRate = options.upgradeCreditDrainRate ?? 1;
    this.upgradeRefreshFraction = options.upgradeRefreshFraction ?? 0.97;
    this.minUpgradeMarginFps = options.minUpgradeMarginFps ?? 2;
    this.maxCreditStepMs = options.maxCreditStepMs ?? 250;
    this.upgradeDwellMs = this.baseUpgradeDwellMs;
    this.effectiveUpgradeFpsValue = this.upgradeAboveFps;
  }

  /**
   * Arm the start-up grace period. Call once, when the loop starts.
   *
   * Without it the governor is steering during the noisiest frames of the whole session and
   * reliably walks a perfectly capable machine down to Low before it has drawn its tenth
   * frame — which is exactly what the milestone-1 test flight hit.
   */
  start(nowMs: number): void {
    this.warmupUntilMs = nowMs + this.warmupMs;
    this.lastUpdateMs = NOT_ARMED;
    this.belowSinceMs = NOT_ARMED;
    this.upgradeCreditMs = 0;
  }

  /** True while the start-up grace period is suppressing both rules. */
  isWarmingUp(nowMs: number): boolean {
    return nowMs < this.warmupUntilMs;
  }

  /**
   * The fps the 1 %-low must beat to earn an upgrade, given what the display can deliver.
   * `achievableFps <= 0` means "not measured yet", and the spec threshold is used as-is.
   */
  effectiveUpgradeFps(achievableFps: number): number {
    if (!(achievableFps > 0)) return this.upgradeAboveFps;
    const capped = Math.min(this.upgradeAboveFps, achievableFps * this.upgradeRefreshFraction);
    // Never let the two thresholds cross, or the governor would have a tier it wants to both
    // raise and lower at the same 1 %-low.
    return Math.max(capped, this.downgradeBelowFps + this.minUpgradeMarginFps);
  }

  /**
   * Feed the governor the current rolling 1 %-low. Returns what it decided.
   *
   * @param achievableFps The best frame rate this machine is actually delivering
   *   (`Profiler.achievableFps`); 0 when not yet known. Caps the upgrade threshold so the rule
   *   stays reachable on a 60 Hz panel.
   *
   * Pass `lowFps <= 0` (no samples yet) and it does nothing.
   */
  update(nowMs: number, lowFps: number, achievableFps = 0): GovernorDecision['action'] {
    const previousUpdateMs = this.lastUpdateMs;
    this.lastUpdateMs = nowMs;
    this.effectiveUpgradeFpsValue = this.effectiveUpgradeFps(achievableFps);

    if (!this.quality.isAuto || !(lowFps > 0) || nowMs < this.warmupUntilMs) {
      this.belowSinceMs = NOT_ARMED;
      this.upgradeCreditMs = 0;
      return 'none';
    }

    if (nowMs < this.settleUntilMs) {
      this.belowSinceMs = NOT_ARMED;
      this.upgradeCreditMs = 0;
      return 'none';
    }

    // Elapsed time since the previous update, clamped: a tab that was backgrounded for a
    // minute must not hand the upgrade rule a minute of credit it never earned.
    const stepMs =
      previousUpdateMs === NOT_ARMED
        ? 0
        : Math.min(Math.max(0, nowMs - previousUpdateMs), this.maxCreditStepMs);

    // --- downgrade: a stopwatch, rearmed by any recovery ---------------------------------
    if (lowFps < this.downgradeBelowFps) {
      if (this.belowSinceMs === NOT_ARMED) this.belowSinceMs = nowMs;
    } else {
      this.belowSinceMs = NOT_ARMED;
    }

    // --- upgrade: a credit balance, drained rather than reset by a dip ---------------------
    if (lowFps > this.effectiveUpgradeFpsValue) {
      this.upgradeCreditMs += stepMs;
      if (this.upgradeCreditMs > this.upgradeDwellMs) this.upgradeCreditMs = this.upgradeDwellMs;
    } else {
      this.upgradeCreditMs -= stepMs * this.upgradeCreditDrainRate;
      if (this.upgradeCreditMs < 0) this.upgradeCreditMs = 0;
    }

    // --- fire ------------------------------------------------------------------------------
    // Downgrade is checked first: when a machine is struggling, dropping a tier is always the
    // more urgent of the two, and the two conditions are mutually exclusive anyway.
    if (
      this.belowSinceMs !== NOT_ARMED &&
      nowMs - this.belowSinceMs >= this.downgradeDwellMs &&
      this.quality.canStepDown()
    ) {
      if (this.lastActionWasUpgrade) {
        // The last upgrade did not hold. Wait longer before trying that again.
        this.upgradeDwellMs = Math.min(this.upgradeDwellMs * 2, this.maxUpgradeDwellMs);
      }
      this.quality.stepDown('governor');
      this.onChanged(nowMs);
      this.lastActionWasUpgrade = false;
      this.downgradeCountValue++;
      return 'down';
    }

    if (
      lowFps > this.effectiveUpgradeFpsValue &&
      this.upgradeCreditMs >= this.upgradeDwellMs &&
      this.quality.canStepUp()
    ) {
      this.quality.stepUp('governor');
      this.onChanged(nowMs);
      this.lastActionWasUpgrade = true;
      this.upgradeCountValue++;
      return 'up';
    }

    return 'none';
  }

  private onChanged(nowMs: number): void {
    this.settleUntilMs = nowMs + this.settleMs;
    this.belowSinceMs = NOT_ARMED;
    this.upgradeCreditMs = 0;
  }

  /** Tell the governor the tier moved for some other reason (user picked a tier). */
  notifyExternalChange(nowMs: number): void {
    this.onChanged(nowMs);
    this.lastActionWasUpgrade = false;
  }

  /** Current dwell required before an upgrade [ms] — grows when upgrades fail to hold. */
  get currentUpgradeDwellMs(): number {
    return this.upgradeDwellMs;
  }

  /** Upgrade threshold in force at the last `update()` [fps]. */
  get currentUpgradeFps(): number {
    return this.effectiveUpgradeFpsValue;
  }

  get downgradeCount(): number {
    return this.downgradeCountValue;
  }

  get upgradeCount(): number {
    return this.upgradeCountValue;
  }

  /**
   * Seconds the current rule has been satisfied — for the overlay's "auto" readout. For the
   * upgrade rule this is the accumulated credit, which is the number that actually decides
   * whether the tier will move, and therefore the number worth showing a pilot.
   */
  pendingSeconds(nowMs: number): number {
    if (this.belowSinceMs !== NOT_ARMED) return (nowMs - this.belowSinceMs) / 1000;
    return this.upgradeCreditMs / 1000;
  }

  /** Which rule the overlay should label {@link pendingSeconds} with. */
  pendingRule(): 'down' | 'up' | 'none' {
    if (this.belowSinceMs !== NOT_ARMED) return 'down';
    return this.upgradeCreditMs > 0 ? 'up' : 'none';
  }

  /** Seconds the pending rule needs before it fires. */
  pendingTargetSeconds(): number {
    if (this.belowSinceMs !== NOT_ARMED) return this.downgradeDwellMs / 1000;
    return this.upgradeDwellMs / 1000;
  }

  reset(): void {
    this.belowSinceMs = NOT_ARMED;
    this.upgradeCreditMs = 0;
    this.lastUpdateMs = NOT_ARMED;
    this.settleUntilMs = Number.NEGATIVE_INFINITY;
    this.warmupUntilMs = Number.NEGATIVE_INFINITY;
    this.upgradeDwellMs = this.baseUpgradeDwellMs;
    this.lastActionWasUpgrade = false;
    this.downgradeCountValue = 0;
    this.upgradeCountValue = 0;
    this.effectiveUpgradeFpsValue = this.upgradeAboveFps;
  }
}
